
'use client'

import { useCallback, useEffect } from 'react'

interface CookiePreferences {
  essential: boolean
  analytics: boolean
  functional: boolean
  marketing: boolean
}

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
  }
}

export default function GoogleAnalytics({ measurementId }: { measurementId: string }) {
  const loadGoogleAnalytics = useCallback(() => {
    // Check if GA is already loaded
    if (typeof window.gtag === 'function') return

    // Load Google Analytics script
    const script1 = document.createElement('script')
    script1.async = true
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}');
    `
    document.head.appendChild(script2)
  }, [measurementId])

  useEffect(() => {
    // Skip if no valid measurement ID configured
    if (!measurementId || measurementId === 'GA_MEASUREMENT_ID' || !measurementId.startsWith('G-')) {
      return
    }

    // Check if analytics cookies are already accepted
    const consent = localStorage.getItem('cookie-consent')
    if (consent) {
      const preferences: CookiePreferences = JSON.parse(consent)
      if (preferences.analytics) {
        loadGoogleAnalytics()
      }
    }

    // Listen for cookie consent changes
    const handleConsentChange = (event: CustomEvent) => {
      if (event.detail?.analytics) {
        loadGoogleAnalytics()
      }
    }

    window.addEventListener('cookie-consent-changed', handleConsentChange as EventListener)

    return () => {
      window.removeEventListener('cookie-consent-changed', handleConsentChange as EventListener)
    }
  }, [loadGoogleAnalytics, measurementId])

  return null
}
